import React, { createContext, useContext, useEffect, useState } from 'react';

const OnboardContext = createContext();

export const useOnboardContext = () => {
  return useContext(OnboardContext);
};

const calculateProgress = (currentStep, totalSteps) => {
  return currentStep * (100 / totalSteps);
};

const initialServiceState = {
  serviceName: '',
  description: '',
  svcTypeId: null,
  recurring: 'N',
  capacity: 1,
  resources: [],
  target: 'all',
  location: {},
  price: 0,
  duration: 60,
  defaultTimeSlotStep: 60,
  preparationTime: 0,
};

const initialCourseState = {
  serviceName: '',
  description: '',
  svcTypeId: null,
  recurring: 'Y',
  capacity: 1,
  minimumCapacity: 0,
  model: 'session',
  resources: [],
  target: 'all',
  location: {},
  price: 0,
  duration: 60,
  preparationTime: 0,
};

const initialState = {
  isLoading: true,
  error: false,
  data: {
    providerType: '',
    providerName: '',
    providerCategory: '',
    providerLocation: {},
    providerSchedules: null,
  },
  service: initialServiceState,
  course: initialCourseState,
  errors: {
    providerLocation: null,
    providerSchedules: null,
  },
  activeStep: 1,
  serviceType: null,
  progress: 0,
  createdProviderId: null,
  createdCourseIds: {
    serviceId: null,
    classId: null,
  },
  finished: {
    service: false,
    course: false,
    event: false,
  },
};

// const numSteps = 6;

const OnboardProvider = ({ children }) => {
  const [ctxState, setCtxState] = useState(initialState);
  const [numSteps, setNumSteps] = useState(6);

  useEffect(() => {
    setCtxState(prev => ({ ...prev, progress: calculateProgress(ctxState.activeStep - 1, numSteps) }));
  }, [ctxState.activeStep, numSteps]);

  useEffect(() => {
    const maxSteps = ctxState.serviceType === 'service' ? 6 : 7;
    setNumSteps(maxSteps);
  }, [ctxState.serviceType]);

  // ONBOARD

  const setPrevState = newState => {
    setCtxState(prev => ({ ...prev, data: { ...prev.data, ...newState }, activeStep: prev.activeStep + 1 }));
  };

  const setPrevStateWithStep = (newState, step) => {
    setCtxState(prev => ({ ...prev, data: { ...prev.data, ...newState }, activeStep: step }));
  };

  const setErrors = newErrors => {
    setCtxState(prev => ({ ...prev, errors: { ...prev.errors, ...newErrors } }));
  };

  const setSchedules = schedules => {
    setCtxState(prev => ({ ...prev, errors: { ...prev.errors, providerSchedules: null }, data: { ...prev.data, providerSchedules: schedules } }));
  };

  const setLocation = location => {
    setCtxState(prev => ({ ...prev, errors: { ...prev.errors, providerLocation: null }, data: { ...prev.data, providerLocation: location } }));
  };

  const setActiveStep = (step, type) => {
    setCtxState(prev => ({ ...prev, activeStep: step, serviceType: type ? type : prev.serviceType }));
  };

  const setFinishServices = step => {
    setCtxState(prev => ({ ...prev, activeStep: step, finished: { ...prev.finished, services: true } }));
  };

  const nextActiveStep = () => {
    setCtxState(prev => ({ ...prev, activeStep: prev.activeStep + 1 }));
  };

  const backActiveStep = () => {
    setCtxState(prev => ({ ...prev, activeStep: prev.activeStep - 1 }));
  };

  const setCreatedProviderId = providerId => {
    setCtxState(prev => ({ ...prev, createdProviderId: providerId }));
  };
  const setCreatedCourseIds = (serviceId, classId) => {
    setCtxState(prev => ({
      ...prev,
      createdCourseIds: {
        serviceId,
        classId,
      },
      activeStep: prev.activeStep + 1,
    }));
  };

  const setError = err => {
    setCtxState(prev => ({ ...prev, error: err }));
  };

  const resetState = () => {
    setCtxState(initialState);
  };

  // SERVICE

  const setServiceState = newState => {
    setCtxState(prev => ({ ...prev, service: { ...prev.service, ...newState }, activeStep: prev.activeStep + 1 }));
  };

  const resetServiceStep = step => {
    setCtxState(prev => ({ ...prev, service: initialServiceState, finished: { ...prev.finished, service: true }, activeStep: step }));
  };

  // COURSE

  const setCourseState = newState => {
    setCtxState(prev => ({ ...prev, course: { ...prev.course, ...newState }, activeStep: prev.activeStep + 1 }));
  };

  const resetCourseStep = (step, type) => {
    setCtxState(prev => ({
      ...prev,
      course: initialCourseState,
      createdCourseIds: {
        serviceId: null,
        classId: null,
      },
      finished: { ...prev.finished, [type]: true },
      activeStep: step,
    }));
  };

  return (
    <OnboardContext.Provider
      value={{
        //
        state: ctxState,
        errors: ctxState.errors,
        activeStep: ctxState.activeStep,
        progress: ctxState.progress,
        schedules: ctxState.data.providerSchedules,
        location: ctxState.data.providerLocation,
        finished: ctxState.finished,
        serviceType: ctxState.serviceType,
        error: ctxState.error,
        createdProviderId: ctxState.createdProviderId,
        createdCourseIds: ctxState.createdCourseIds,
        maxSteps: numSteps,
        setPrevState,
        setErrors,
        setPrevStateWithStep,
        setSchedules,
        setFinishServices,
        setLocation,
        setActiveStep,
        setCreatedProviderId,
        setCreatedCourseIds,
        nextActiveStep,
        backActiveStep,
        setError,
        resetState,
        //service
        serviceState: ctxState.service,
        setServiceState,
        resetServiceStep,
        //course
        courseState: ctxState.course,
        setCourseState,
        resetCourseStep,
      }}
    >
      {children}
    </OnboardContext.Provider>
  );
};

export { OnboardProvider, OnboardContext };
